import React, { useState, useEffect } from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useLocation, Link } from "react-router-dom";
import Loading from "../components/Loading";
import { fetchOrganisationsForUser, createOrganisation, deleteOrganisation } from "../utils/apiService";

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'teams', label: 'Teams' },
];

const Field = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd' }}>
      {label}
    </div>
    <div style={{ fontSize: '0.9rem', color: '#1a1d23', fontWeight: 500 }}>
      {value || <span style={{ color: '#ced4da' }}>—</span>}
    </div>
  </div>
);

const ProfileTab = ({ user }) => (
  <Row className="g-3">
    <Col xs={12} md={4}>
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', height: '100%' }}>
        <CardBody style={{ padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <img
            src={user.picture}
            alt="Profile"
            style={{ width: '88px', height: '88px', borderRadius: '50%', objectFit: 'cover', marginBottom: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          />
          <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1d23', marginBottom: '0.25rem' }}>
            {user.name}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '1rem' }}>
            {user.email}
          </div>
          {user.email_verified && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '20px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '0.72rem', fontWeight: 600, color: '#16a34a' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Verified
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
    <Col xs={12} md={8}>
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', height: '100%' }}>
        <CardBody style={{ padding: '1.75rem' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1d23', marginBottom: '1.25rem' }}>
            Account Information
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
            <Field label="Full Name" value={user.name} />
            <Field label="Nickname" value={user.nickname} />
            <Field label="Email" value={user.email} />
            <Field label="Email Verified" value={user.email_verified ? 'Yes' : 'No'} />
            <Field label="Auth Provider" value={user.sub?.split('|')[0]} />
            <Field label="User ID" value={user.sub?.split('|')[1]} />
            {user.updated_at && (
              <Field
                label="Last Updated"
                value={new Date(user.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              />
            )}
            {user.locale && <Field label="Locale" value={user.locale} />}
          </div>
        </CardBody>
      </Card>
    </Col>
  </Row>
);

const ConfirmDeleteModal = ({ name, onConfirm, onCancel, deleting }) => (
  <>
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1060 }} onClick={onCancel} />
    <div style={{
      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      background: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      zIndex: 1070, width: '100%', maxWidth: '400px', padding: '2rem',
    }}>
      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1d23', marginBottom: '0.5rem' }}>Delete team</div>
      <p style={{ fontSize: '0.875rem', color: '#4a5568', marginBottom: '1.5rem' }}>
        Are you sure you want to delete <strong>{name}</strong>? This action cannot be undone.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} disabled={deleting} className="btn-pill btn-pill-grey" style={{ fontSize: '0.82rem', padding: '5px 16px' }}>
          Cancel
        </button>
        <button onClick={onConfirm} disabled={deleting} className="btn-pill btn-pill-red" style={{ fontSize: '0.82rem', padding: '5px 16px' }}>
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  </>
);

const AddOrganisationPanel = ({ onClose, onAdded }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [saving, setSaving] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      await createOrganisation({ orgName }, token);
      onAdded();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create team');
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1040 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '360px', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 1050, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1d23' }}>New team</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: '4px', display: 'flex', alignItems: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>
              Organisation Name <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <input
              type="text"
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              placeholder="Enter team name"
              required
              autoFocus
              style={{ fontSize: '0.875rem', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '7px', outline: 'none', color: '#1a1d23' }}
            />
          </div>
          {error && (
            <div style={{ fontSize: '0.8rem', color: 'var(--color-danger)', padding: '8px 12px', background: '#fff5f5', borderRadius: '6px', border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}
        </form>
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '0.75rem' }}>
          <button type="button" onClick={onClose} style={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, padding: '9px', borderRadius: '7px', border: '1px solid var(--color-border)', background: 'white', color: '#4a5568', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving} style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, padding: '9px', borderRadius: '7px', border: 'none', background: 'var(--color-primary)', color: 'white', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </>
  );
};

const OrganisationsTab = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      const data = await fetchOrganisationsForUser(token).catch(() => null);
      const list = data?.completeOrgList || data?.values || (Array.isArray(data) ? data : []);
      setOrgs(list);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      await deleteOrganisation(id, token);
      setConfirmDeleteId(null);
      await load();
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => { load(); }, [getAccessTokenSilently]);

  return (
    <>
      {panelOpen && <AddOrganisationPanel onClose={() => setPanelOpen(false)} onAdded={load} />}
      {confirmDeleteId && (() => {
        const org = orgs.find(o => (o.orgId || o.id) === confirmDeleteId);
        return <ConfirmDeleteModal
          name={org?.orgName || org?.name || 'this team'}
          onConfirm={() => handleDelete(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
          deleting={!!deletingId}
        />;
      })()}
    <Card style={{ border: '1px solid var(--color-border)', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1d23' }}>
          {loading ? 'Loading...' : `${orgs.length} team${orgs.length !== 1 ? 's' : ''}`}
        </div>
        <button
          onClick={() => setPanelOpen(true)}
          style={{ fontSize: '0.78rem', fontWeight: 600, padding: '4px 12px', borderRadius: '6px', border: '1px solid var(--color-primary)', background: 'white', color: 'var(--color-primary)', cursor: 'pointer' }}
        >
          New Organisation
        </button>
      </div>
      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#adb5bd', fontSize: '0.875rem' }}>
          <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
          <div>Loading teams...</div>
        </div>
      ) : (
        <table className="table mb-0" style={{ fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#fafbfc', borderBottom: '1px solid #e9ecef' }}>
              {[['ID', '80px'], ['Name', null], ['Created', '160px'], ['', '160px']].map(([label, width]) => (
                <th key={label} style={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', padding: '0.75rem 1.25rem', border: 'none', whiteSpace: 'nowrap', ...(width ? { width, minWidth: width } : {}) }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orgs.length > 0 ? orgs.map((org, i) => {
              const id = org.orgId || org.id;
              const name = org.orgName || org.name || '—';
              const created = org.createdOn_UTC || org.CreatedOn_UTC || org.createdAt || org.CreatedAt;
              return (
                <tr key={i} style={{ borderBottom: '1px solid #f7f8fa', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fafbfc'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                >
                  <td style={{ padding: '0.85rem 1.25rem', color: '#adb5bd', fontWeight: 500, verticalAlign: 'middle' }}>
                    {id ? `#${id}` : '—'}
                  </td>
                  <td style={{ padding: '0.85rem 1.25rem', fontWeight: 600, color: '#1a1d23', verticalAlign: 'middle' }}>
                    {name}
                  </td>
                  <td style={{ padding: '0.85rem 1.25rem', color: '#4a5568', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                    {created ? new Date(created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '0.85rem 1.25rem', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {id && <Link to={`/team/${id}`} className="btn-pill btn-pill-blue" style={{ fontSize: '0.75rem', padding: '3px 10px' }}>View</Link>}
                      {id && <button onClick={() => setConfirmDeleteId(id)} className="btn-pill btn-pill-red" style={{ fontSize: '0.75rem', padding: '3px 10px' }}>Delete</button>}
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="4" style={{ padding: '4rem', textAlign: 'center', color: '#adb5bd', fontSize: '0.875rem' }}>
                  No organisations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </Card>
    </>
  );
};


const SettingsComponent = () => {
  const { user } = useAuth0();
  const location = useLocation();
  const requestedTab = new URLSearchParams(location.search).get('tab');
  const initialTab = requestedTab === 'organisations' ? 'teams' : (requestedTab || 'profile');
  const [activeTab, setActiveTab] = useState(initialTab);

  return (
    <div style={{ paddingBottom: '3rem' }}>
      <div className="mb-4">
        <h2 className="page-title mb-1">Settings</h2>
        <p className="page-subtitle">Manage your account and preferences</p>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '1.5rem' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
              padding: '0.6rem 1.1rem',
              fontSize: '0.88rem',
              fontWeight: activeTab === tab.id ? 600 : 500,
              color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
              cursor: 'pointer',
              marginBottom: '-1px',
              transition: 'color 0.15s ease, border-color 0.15s ease',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && <ProfileTab user={user} />}
      {activeTab === 'teams' && <OrganisationsTab />}
    </div>
  );
};

export default withAuthenticationRequired(SettingsComponent, {
  onRedirecting: () => <Loading />,
});
